/**
 * ApplicationsToolbar — the queue-wide actions from the legacy console:
 * a bulk reminder to everyone with an incomplete application, and the global
 * "contractors must submit KYC documents" switch that governs whether those
 * document requirements apply at all.
 *
 * Both are page-level rather than per-application, so they live above the
 * table instead of in ApplicationActions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { NotificationsActive, Policy } from '@mui/icons-material';

import adminApplicationsService from '../../services/adminApplicationsService';
import { panelSx } from './surfaces';
import { text } from '../../theme/colors';

// remind-incomplete's validator accepts only these (plus 'all'), so a
// quality_inspector filter has to fall back rather than 400.
const BULK_REMINDER_ROLES = ['contractor', 'verifier'];

const ApplicationsToolbar = ({ roleFilter = 'all', onChanged }) => {
  const remindRole = BULK_REMINDER_ROLES.includes(roleFilter) ? roleFilter : 'all';
  const [kycRequired, setKycRequired] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const res = await adminApplicationsService.getKycSettings();
      const value = res?.data?.contractorKycRequired;
      setKycRequired(typeof value === 'boolean' ? value : null);
    } catch {
      setKycRequired(null); // Non-fatal: the switch simply doesn't render.
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const toggleKyc = async (next) => {
    setBusy(true);
    setError(null);
    try {
      await adminApplicationsService.setKycSettings(next);
      setKycRequired(next);
      setNotice(
        next
          ? 'Contractors must now submit KYC documents.'
          : 'Contractor KYC documents are no longer required.'
      );
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not change that setting.');
    } finally {
      setBusy(false);
    }
  };

  const sendBulkReminder = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await adminApplicationsService.remindIncomplete(remindRole);
      setDialog(null);
      setNotice(res?.message || 'Reminders sent.');
      onChanged?.();
      window.dispatchEvent(new Event('eqaya:unread-refresh'));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not send reminders.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && !dialog && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          p: 1.75,
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<NotificationsActive />}
          onClick={() => {
            setError(null);
            setDialog('remind');
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Remind everyone incomplete
          {remindRole !== 'all' ? ` (${remindRole}s)` : ''}
        </Button>

        {typeof kycRequired === 'boolean' && (
          <Tooltip
            title="When off, contractors can be approved without uploading KYC documents."
            arrow
          >
            <FormControlLabel
              control={
                <Switch
                  checked={kycRequired}
                  disabled={busy}
                  onChange={(e) => toggleKyc(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: text.secondary }}>
                  <Policy sx={{ fontSize: 17 }} />
                  Contractor KYC required
                </Box>
              }
              sx={{ mr: 0 }}
            />
          </Tooltip>
        )}
      </Paper>

      <Dialog open={dialog === 'remind'} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Send reminders?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Emails every applicant with an incomplete application
            {remindRole !== 'all' ? ` in the ${remindRole} role` : ''}. This can be a lot of mail —
            it is not limited to the rows on this page.
            {roleFilter !== 'all' && remindRole === 'all'
              ? ` Bulk reminders don't support the ${roleFilter.replace(/_/g, ' ')} role, so this
                 covers every role.`
              : ''}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(null)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={sendBulkReminder}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <NotificationsActive />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Send reminders
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationsToolbar;
